import React from 'react';

/**
 * Athletes Launch 2.0 — Custom Module Icons
 *
 * Each icon is a hand-crafted inline SVG that can be sized and colored via props.
 * Usage: <FusionIcon size={24} color="#d4a017" />
 */

/* ─── Fusion (Social Aggregator) ─── */
/* A chain-link / hub radiating outward — represents connectivity */
export function FusionIcon({ size = 24, color = 'currentColor', style, className }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} className={className}>
            {/* Central hub */}
            <circle cx="16" cy="16" r="5" stroke={color} strokeWidth="2.2" fill="none" />
            {/* Orbiting nodes */}
            <circle cx="6" cy="8" r="2.5" fill={color} opacity="0.9" />
            <circle cx="26" cy="8" r="2.5" fill={color} opacity="0.9" />
            <circle cx="6" cy="24" r="2.5" fill={color} opacity="0.9" />
            <circle cx="26" cy="24" r="2.5" fill={color} opacity="0.9" />
            {/* Connecting lines */}
            <line x1="8" y1="9.5" x2="12.5" y2="13" stroke={color} strokeWidth="1.5" opacity="0.5" />
            <line x1="24" y1="9.5" x2="19.5" y2="13" stroke={color} strokeWidth="1.5" opacity="0.5" />
            <line x1="8" y1="22.5" x2="12.5" y2="19" stroke={color} strokeWidth="1.5" opacity="0.5" />
            <line x1="24" y1="22.5" x2="19.5" y2="19" stroke={color} strokeWidth="1.5" opacity="0.5" />
            {/* Center dot */}
            <circle cx="16" cy="16" r="2" fill={color} />
        </svg>
    );
}

/* ─── Vault (Digital Twin / AI Studio) ─── */
/* A shield with a keyhole — represents security & likeness protection */
export function VaultIcon({ size = 24, color = 'currentColor', style, className }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} className={className}>
            {/* Shield outline */}
            <path
                d="M16 3L5 8V15C5 22.18 9.84 28.78 16 30C22.16 28.78 27 22.18 27 15V8L16 3Z"
                stroke={color}
                strokeWidth="2"
                fill="none"
                strokeLinejoin="round"
            />
            {/* Keyhole circle */}
            <circle cx="16" cy="14" r="3.5" fill={color} opacity="0.9" />
            {/* Keyhole slot */}
            <path d="M14.5 17L16 23L17.5 17" fill={color} opacity="0.9" />
            {/* AI sparkle */}
            <path d="M23 5L24 7L25 5L24 3Z" fill={color} opacity="0.6" />
            <path d="M22 7L23 8L24 7L23 6Z" fill={color} opacity="0.4" />
        </svg>
    );
}

/* ─── Athletes (Database / Matchmaking) ─── */
/* A grid with a magnifying glass — represents search & data */
export function MatrixIcon({ size = 24, color = 'currentColor', style, className }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} className={className}>
            {/* Grid dots — 3x3 matrix */}
            <circle cx="8" cy="8" r="2" fill={color} opacity="0.5" />
            <circle cx="16" cy="8" r="2" fill={color} opacity="0.5" />
            <circle cx="24" cy="8" r="2" fill={color} opacity="0.7" />
            <circle cx="8" cy="16" r="2" fill={color} opacity="0.5" />
            <circle cx="16" cy="16" r="2" fill={color} opacity="0.9" />
            <circle cx="24" cy="16" r="2" fill={color} opacity="0.5" />
            <circle cx="8" cy="24" r="2" fill={color} opacity="0.7" />
            <circle cx="16" cy="24" r="2" fill={color} opacity="0.5" />
            {/* Magnifying glass overlay */}
            <circle cx="22" cy="22" r="5" stroke={color} strokeWidth="2" fill="none" opacity="0.9" />
            <line x1="25.5" y1="25.5" x2="29" y2="29" stroke={color} strokeWidth="2.5" strokeLinecap="round" opacity="0.9" />
            {/* Scan line */}
            <line x1="19" y1="22" x2="25" y2="22" stroke={color} strokeWidth="1" opacity="0.4" />
        </svg>
    );
}

/* ─── Rocket (Revenue Engine / CRM) ─── */
/* A rocket ship — unmistakable rocket silhouette with fins & flame */
export function RocketIcon({ size = 24, color = 'currentColor', style, className }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} className={className}>
            {/* Rocket body — tall capsule shape */}
            <path
                d="M16 2C16 2 11 8 11 17V21H21V17C21 8 16 2 16 2Z"
                stroke={color}
                strokeWidth="2"
                fill="none"
                strokeLinejoin="round"
            />
            {/* Porthole window */}
            <circle cx="16" cy="13" r="2.5" stroke={color} strokeWidth="1.5" fill="none" />
            <circle cx="16" cy="13" r="1" fill={color} opacity="0.6" />
            {/* Left fin */}
            <path d="M11 17L6 23L11 21Z" stroke={color} strokeWidth="1.5" fill={color} opacity="0.5" strokeLinejoin="round" />
            {/* Right fin */}
            <path d="M21 17L26 23L21 21Z" stroke={color} strokeWidth="1.5" fill={color} opacity="0.5" strokeLinejoin="round" />
            {/* Nozzle base */}
            <path d="M12 21L13 23H19L20 21" stroke={color} strokeWidth="1.5" fill="none" strokeLinejoin="round" />
            {/* Exhaust flame — outer */}
            <path d="M13.5 23L16 30L18.5 23" fill={color} opacity="0.85" />
            {/* Exhaust flame — inner */}
            <path d="M14.8 23L16 27.5L17.2 23" fill={color} opacity="0.4" />
            {/* Nose tip accent */}
            <circle cx="16" cy="5.5" r="1" fill={color} opacity="0.5" />
        </svg>
    );
}

/* ─── Trust (Wellness / Reviews) ─── */
/* A heart with a shield checkmark — represents trust & health */
export function TrustIcon({ size = 24, color = 'currentColor', style, className }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} className={className}>
            {/* Heart */}
            <path
                d="M16 28L4.5 17C1.5 14 1.5 9 4.5 6C7.5 3 12 3 15 6L16 7L17 6C20 3 24.5 3 27.5 6C30.5 9 30.5 14 27.5 17L16 28Z"
                stroke={color}
                strokeWidth="2"
                fill="none"
                strokeLinejoin="round"
            />
            {/* Shield checkmark inside */}
            <path
                d="M12 15L15 18L21 12"
                stroke={color}
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
            />
            {/* Pulse dot */}
            <circle cx="24" cy="8" r="1.5" fill={color} opacity="0.6" />
        </svg>
    );
}

/* ─── Additional utility icons ─── */

export function DashboardIcon({ size = 24, color = 'currentColor', style, className }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} className={className}>
            <rect x="4" y="4" width="10" height="12" rx="3" stroke={color} strokeWidth="2" fill="none" />
            <rect x="18" y="4" width="10" height="8" rx="3" stroke={color} strokeWidth="2" fill="none" />
            <rect x="4" y="20" width="10" height="8" rx="3" stroke={color} strokeWidth="2" fill="none" />
            <rect x="18" y="16" width="10" height="12" rx="3" stroke={color} strokeWidth="2" fill="none" />
        </svg>
    );
}

export function MediaIcon({ size = 24, color = 'currentColor', style, className }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} className={className}>
            <rect x="4" y="6" width="24" height="20" rx="4" stroke={color} strokeWidth="2" fill="none" />
            <path d="M13 12L21 16L13 20V12Z" fill={color} opacity="0.85" />
        </svg>
    );
}

export function EventsIcon({ size = 24, color = 'currentColor', style, className }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} className={className}>
            <rect x="4" y="6" width="24" height="22" rx="4" stroke={color} strokeWidth="2" fill="none" />
            <line x1="4" y1="13" x2="28" y2="13" stroke={color} strokeWidth="2" />
            <line x1="10" y1="6" x2="10" y2="3" stroke={color} strokeWidth="2" strokeLinecap="round" />
            <line x1="22" y1="6" x2="22" y2="3" stroke={color} strokeWidth="2" strokeLinecap="round" />
            <circle cx="11" cy="19" r="1.5" fill={color} opacity="0.7" />
            <circle cx="16" cy="19" r="1.5" fill={color} opacity="0.7" />
            <circle cx="21" cy="19" r="1.5" fill={color} opacity="0.7" />
            <circle cx="11" cy="24" r="1.5" fill={color} opacity="0.5" />
            <circle cx="16" cy="24" r="1.5" fill={color} opacity="0.5" />
        </svg>
    );
}

/* ─── Explore (Unified Feed / Discover) ─── */
/* A compass with radiating rays — represents discovery & exploration */
export function ExploreIcon({ size = 24, color = 'currentColor', style, className }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} className={className}>
            {/* Outer ring */}
            <circle cx="16" cy="16" r="12" stroke={color} strokeWidth="2" fill="none" />
            {/* Compass diamond */}
            <path d="M16 6L19 13L16 14L13 13Z" fill={color} opacity="0.9" />
            <path d="M16 26L13 19L16 18L19 19Z" fill={color} opacity="0.5" />
            <path d="M6 16L13 13L14 16L13 19Z" fill={color} opacity="0.7" />
            <path d="M26 16L19 19L18 16L19 13Z" fill={color} opacity="0.7" />
            {/* Center dot */}
            <circle cx="16" cy="16" r="2" fill={color} />
            {/* Cardinal ticks */}
            <line x1="16" y1="2" x2="16" y2="4" stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity="0.6" />
            <line x1="16" y1="28" x2="16" y2="30" stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity="0.6" />
            <line x1="2" y1="16" x2="4" y2="16" stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity="0.6" />
            <line x1="28" y1="16" x2="30" y2="16" stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity="0.6" />
        </svg>
    );
}

/* ─── Sponsorships (Brand Partnerships) ─── */
/* A handshake with a star — represents deals & partnerships */
export function SponsorshipsIcon({ size = 24, color = 'currentColor', style, className }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} className={className}>
            {/* Star burst */}
            <path d="M16 3L18.5 11L27 11L20 16L22.5 24L16 19.5L9.5 24L12 16L5 11L13.5 11Z"
                stroke={color} strokeWidth="2" fill="none" strokeLinejoin="round" />
            {/* Inner star fill */}
            <path d="M16 8L17.5 12.5L22 12.5L18.5 15.5L19.8 20L16 17.2L12.2 20L13.5 15.5L10 12.5L14.5 12.5Z"
                fill={color} opacity="0.5" />
            {/* Handshake lines */}
            <line x1="8" y1="22" x2="13" y2="18" stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity="0.7" />
            <line x1="24" y1="22" x2="19" y2="18" stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity="0.7" />
            {/* Base dots */}
            <circle cx="7" cy="23" r="1.5" fill={color} opacity="0.6" />
            <circle cx="25" cy="23" r="1.5" fill={color} opacity="0.6" />
        </svg>
    );
}

/* ───────────────────────────────────────────────────────────────────── */
/*  Vault Page — Custom Icons (replacing emojis)                       */
/* ───────────────────────────────────────────────────────────────────── */

/* ─── Robot / Digital Twin ─── */
export function RobotIcon({ size = 24, color = 'currentColor', style, className }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} className={className}>
            {/* Antenna */}
            <line x1="16" y1="2" x2="16" y2="7" stroke={color} strokeWidth="2" strokeLinecap="round" />
            <circle cx="16" cy="2" r="1.5" fill={color} opacity="0.8" />
            {/* Head */}
            <rect x="7" y="7" width="18" height="14" rx="4" stroke={color} strokeWidth="2" fill="none" />
            {/* Eyes */}
            <circle cx="12" cy="14" r="2.5" fill={color} opacity="0.85" />
            <circle cx="20" cy="14" r="2.5" fill={color} opacity="0.85" />
            {/* Mouth */}
            <line x1="12" y1="18" x2="20" y2="18" stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity="0.5" />
            {/* Body */}
            <rect x="9" y="23" width="14" height="6" rx="3" stroke={color} strokeWidth="2" fill="none" />
            {/* Neck */}
            <line x1="14" y1="21" x2="14" y2="23" stroke={color} strokeWidth="1.5" opacity="0.6" />
            <line x1="18" y1="21" x2="18" y2="23" stroke={color} strokeWidth="1.5" opacity="0.6" />
        </svg>
    );
}

/* ─── Clapperboard / Video ─── */
export function ClapperboardIcon({ size = 24, color = 'currentColor', style, className }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} className={className}>
            <rect x="4" y="10" width="24" height="18" rx="3" stroke={color} strokeWidth="2" fill="none" />
            {/* Clapper top */}
            <path d="M4 13L28 13" stroke={color} strokeWidth="2" />
            <path d="M4 10L6 4L12 6L10 10" fill={color} opacity="0.6" />
            <path d="M10 10L12 4L18 6L16 10" fill={color} opacity="0.4" />
            <path d="M16 10L18 4L24 6L22 10" fill={color} opacity="0.6" />
            <path d="M22 10L24 4L28 5.5L28 10" fill={color} opacity="0.4" />
            {/* Play triangle */}
            <path d="M14 18L20 21.5L14 25V18Z" fill={color} opacity="0.7" />
        </svg>
    );
}

/* ─── Camera / Photo ─── */
export function CameraIcon({ size = 24, color = 'currentColor', style, className }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} className={className}>
            <rect x="3" y="10" width="26" height="18" rx="4" stroke={color} strokeWidth="2" fill="none" />
            {/* Lens */}
            <circle cx="16" cy="19" r="5.5" stroke={color} strokeWidth="2" fill="none" />
            <circle cx="16" cy="19" r="2.5" fill={color} opacity="0.7" />
            {/* Viewfinder bump */}
            <path d="M12 10L14 6H18L20 10" stroke={color} strokeWidth="2" fill="none" strokeLinejoin="round" />
            {/* Flash */}
            <circle cx="24" cy="14" r="1.5" fill={color} opacity="0.5" />
        </svg>
    );
}

/* ─── Phone / Mobile ─── */
export function PhoneIcon({ size = 24, color = 'currentColor', style, className }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} className={className}>
            <rect x="8" y="3" width="16" height="26" rx="4" stroke={color} strokeWidth="2" fill="none" />
            {/* Screen */}
            <rect x="10" y="7" width="12" height="16" rx="1" fill={color} opacity="0.15" />
            {/* Home indicator */}
            <line x1="13" y1="26" x2="19" y2="26" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.6" />
            {/* Camera notch */}
            <circle cx="16" cy="5" r="1" fill={color} opacity="0.5" />
        </svg>
    );
}

/* ─── Sparkles / AI Generate ─── */
export function SparklesIcon({ size = 24, color = 'currentColor', style, className }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} className={className}>
            {/* Main sparkle */}
            <path d="M16 4L18.5 12.5L27 14L18.5 15.5L16 24L13.5 15.5L5 14L13.5 12.5Z" fill={color} opacity="0.9" />
            {/* Small sparkle top-right */}
            <path d="M25 4L26 7L29 8L26 9L25 12L24 9L21 8L24 7Z" fill={color} opacity="0.6" />
            {/* Small sparkle bottom-left */}
            <path d="M8 22L9 24.5L11.5 25.5L9 26.5L8 29L7 26.5L4.5 25.5L7 24.5Z" fill={color} opacity="0.5" />
        </svg>
    );
}

/* ─── Lock / Security ─── */
export function LockIcon({ size = 24, color = 'currentColor', style, className }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} className={className}>
            {/* Lock body */}
            <rect x="7" y="14" width="18" height="14" rx="4" stroke={color} strokeWidth="2" fill="none" />
            {/* Shackle */}
            <path d="M11 14V10C11 7.24 13.24 5 16 5C18.76 5 21 7.24 21 10V14" stroke={color} strokeWidth="2" strokeLinecap="round" fill="none" />
            {/* Keyhole */}
            <circle cx="16" cy="20" r="2" fill={color} opacity="0.8" />
            <path d="M15 21.5L16 25L17 21.5" fill={color} opacity="0.8" />
        </svg>
    );
}

/* ─── Photo (simple) ─── */
export function PhotoIcon({ size = 24, color = 'currentColor', style, className }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} className={className}>
            <rect x="4" y="6" width="24" height="20" rx="4" stroke={color} strokeWidth="2" fill="none" />
            {/* Mountain landscape */}
            <path d="M4 22L11 15L16 20L20 16L28 22" stroke={color} strokeWidth="2" strokeLinejoin="round" fill="none" opacity="0.7" />
            {/* Sun */}
            <circle cx="22" cy="12" r="3" fill={color} opacity="0.6" />
        </svg>
    );
}

/* ─── Upload ─── */
export function UploadIcon({ size = 24, color = 'currentColor', style, className }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} className={className}>
            {/* Arrow up */}
            <path d="M16 4L9 12H13V20H19V12H23L16 4Z" fill={color} opacity="0.8" />
            {/* Base tray */}
            <path d="M6 22V26C6 27.1 6.9 28 8 28H24C25.1 28 26 27.1 26 26V22" stroke={color} strokeWidth="2" strokeLinecap="round" fill="none" />
        </svg>
    );
}

/* ─── Gallery / Image Collection ─── */
export function GalleryIcon({ size = 24, color = 'currentColor', style, className }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} className={className}>
            {/* Back card */}
            <rect x="6" y="4" width="22" height="18" rx="3" stroke={color} strokeWidth="1.5" fill="none" opacity="0.4" />
            {/* Front card */}
            <rect x="4" y="8" width="22" height="18" rx="3" stroke={color} strokeWidth="2" fill="none" />
            {/* Mountain */}
            <path d="M4 22L10 15L14 19L18 14L26 22" stroke={color} strokeWidth="1.5" strokeLinejoin="round" fill="none" opacity="0.6" />
            {/* Sun */}
            <circle cx="20" cy="14" r="2.5" fill={color} opacity="0.5" />
        </svg>
    );
}

/* ─── Download ─── */
export function DownloadIcon({ size = 24, color = 'currentColor', style, className }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} className={className}>
            {/* Arrow down */}
            <path d="M16 4V20" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
            <path d="M10 16L16 22L22 16" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            {/* Base tray */}
            <path d="M6 24V26C6 27.1 6.9 28 8 28H24C25.1 28 26 27.1 26 26V24" stroke={color} strokeWidth="2" strokeLinecap="round" fill="none" />
        </svg>
    );
}

/* ─── Share / Export ─── */
export function ShareIcon({ size = 24, color = 'currentColor', style, className }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} className={className}>
            {/* Top node */}
            <circle cx="24" cy="8" r="4" stroke={color} strokeWidth="2" fill="none" />
            {/* Middle node */}
            <circle cx="8" cy="16" r="4" stroke={color} strokeWidth="2" fill="none" />
            {/* Bottom node */}
            <circle cx="24" cy="24" r="4" stroke={color} strokeWidth="2" fill="none" />
            {/* Connecting lines */}
            <line x1="11.5" y1="14" x2="20.5" y2="10" stroke={color} strokeWidth="1.8" opacity="0.6" />
            <line x1="11.5" y1="18" x2="20.5" y2="22" stroke={color} strokeWidth="1.8" opacity="0.6" />
        </svg>
    );
}

/* ─── Tag / Label ─── */
export function TagIcon({ size = 24, color = 'currentColor', style, className }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} className={className}>
            <path
                d="M4 5H15L28 18L18 28L5 15V5Z"
                stroke={color} strokeWidth="2" fill="none" strokeLinejoin="round"
            />
            {/* Hole */}
            <circle cx="10" cy="10" r="2.5" fill={color} opacity="0.7" />
            {/* Price line */}
            <line x1="14" y1="20" x2="20" y2="14" stroke={color} strokeWidth="1.5" opacity="0.4" strokeLinecap="round" />
        </svg>
    );
}

/* ─── Check / Checkmark ─── */
export function CheckIcon({ size = 24, color = 'currentColor', style, className }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} className={className}>
            <path d="M8 16L14 22L24 10" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </svg>
    );
}

/* ── Product Icons for Apex Store ── */

/* ─── Tee Shirt ─── */
export function TeeIcon({ size = 24, color = 'currentColor', style, className }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} className={className}>
            {/* Shirt body + sleeves */}
            <path
                d="M10 6L6 8L3 14L7 16L9 12V28H23V12L25 16L29 14L26 8L22 6"
                stroke={color} strokeWidth="2" fill="none" strokeLinejoin="round"
            />
            {/* Collar */}
            <path d="M10 6C10 6 12 9 16 9C20 9 22 6 22 6" stroke={color} strokeWidth="2" fill="none" />
        </svg>
    );
}

/* ─── Shorts ─── */
export function ShortsIcon({ size = 24, color = 'currentColor', style, className }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} className={className}>
            <path
                d="M5 8H27V14L22 26H18L16 18L14 26H10L5 14V8Z"
                stroke={color} strokeWidth="2" fill="none" strokeLinejoin="round"
            />
            {/* Waistband */}
            <line x1="5" y1="11" x2="27" y2="11" stroke={color} strokeWidth="1.5" opacity="0.4" />
            {/* Drawstring */}
            <path d="M14 8V10" stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity="0.5" />
            <path d="M18 8V10" stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity="0.5" />
        </svg>
    );
}

/* ─── Drink / Beverage ─── */
export function DrinkIcon({ size = 24, color = 'currentColor', style, className }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} className={className}>
            {/* Cup */}
            <path d="M9 8L11 28H21L23 8" stroke={color} strokeWidth="2" fill="none" strokeLinejoin="round" />
            {/* Lid */}
            <rect x="7" y="5" width="18" height="4" rx="2" stroke={color} strokeWidth="2" fill="none" />
            {/* Straw */}
            <line x1="18" y1="2" x2="16" y2="10" stroke={color} strokeWidth="2" strokeLinecap="round" />
            {/* Liquid line */}
            <path d="M10.5 16H21.5" stroke={color} strokeWidth="1.5" opacity="0.4" strokeLinecap="round" />
        </svg>
    );
}

/* ─── Headband ─── */
export function HeadbandIcon({ size = 24, color = 'currentColor', style, className }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} className={className}>
            {/* Head outline */}
            <ellipse cx="16" cy="16" rx="11" ry="13" stroke={color} strokeWidth="1.5" fill="none" opacity="0.3" />
            {/* Headband */}
            <path d="M5 13C5 13 8 8 16 8C24 8 27 13 27 13" stroke={color} strokeWidth="3" strokeLinecap="round" fill="none" />
            <path d="M5 13C5 13 8 18 16 18C24 18 27 13 27 13" stroke={color} strokeWidth="3" strokeLinecap="round" fill="none" />
            {/* Logo mark */}
            <circle cx="16" cy="13" r="2" fill={color} opacity="0.7" />
        </svg>
    );
}

/* ─── Shaker Bottle ─── */
export function ShakerIcon({ size = 24, color = 'currentColor', style, className }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} className={className}>
            {/* Bottle body */}
            <path d="M10 12L9 28H23L22 12" stroke={color} strokeWidth="2" fill="none" strokeLinejoin="round" />
            {/* Cap */}
            <rect x="10" y="7" width="12" height="5" rx="2" stroke={color} strokeWidth="2" fill="none" />
            {/* Spout */}
            <path d="M14 7V4H18V7" stroke={color} strokeWidth="1.5" fill="none" />
            {/* Measurement lines */}
            <line x1="11" y1="18" x2="14" y2="18" stroke={color} strokeWidth="1.2" opacity="0.4" />
            <line x1="11" y1="22" x2="14" y2="22" stroke={color} strokeWidth="1.2" opacity="0.4" />
            <line x1="10" y1="26" x2="14" y2="26" stroke={color} strokeWidth="1.2" opacity="0.4" />
        </svg>
    );
}

/* ─── Supplement / Pill ─── */
export function SupplementIcon({ size = 24, color = 'currentColor', style, className }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} className={className}>
            {/* Capsule */}
            <rect x="6" y="10" width="20" height="12" rx="6" stroke={color} strokeWidth="2" fill="none" />
            {/* Divider */}
            <line x1="16" y1="10" x2="16" y2="22" stroke={color} strokeWidth="2" />
            {/* Left half fill */}
            <path d="M6 16C6 12.7 8.7 10 12 10H16V22H12C8.7 22 6 19.3 6 16Z" fill={color} opacity="0.3" />
            {/* Sparkle — potency */}
            <path d="M24 6L25 8L27 9L25 10L24 12L23 10L21 9L23 8Z" fill={color} opacity="0.5" />
        </svg>
    );
}

/* ─── Connections (Athlete Network) ─── */
/* Two people with a connecting link — represents athlete networking */
export function ConnectionsIcon({ size = 24, color = 'currentColor', style, className }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} className={className}>
            {/* Left person */}
            <circle cx="9" cy="10" r="3.5" stroke={color} strokeWidth="2" fill="none" />
            <path d="M3 22C3 18.7 5.7 16 9 16C12.3 16 15 18.7 15 22" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" />
            {/* Right person */}
            <circle cx="23" cy="10" r="3.5" stroke={color} strokeWidth="2" fill="none" />
            <path d="M17 22C17 18.7 19.7 16 23 16C26.3 16 29 18.7 29 22" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" />
            {/* Connection link */}
            <line x1="13" y1="10" x2="19" y2="10" stroke={color} strokeWidth="2" strokeDasharray="2 2" opacity="0.6" />
            {/* Spark */}
            <circle cx="16" cy="10" r="1.5" fill={color} opacity="0.8" />
            {/* Network pulse */}
            <circle cx="16" cy="10" r="4" stroke={color} strokeWidth="1" opacity="0.3" />
        </svg>
    );
}

/* ─── Marketplace (Product Storefront) ─── */
/* A storefront with awning — represents shopping & products */
export function MarketplaceIcon({ size = 24, color = 'currentColor', style, className }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} className={className}>
            {/* Storefront body */}
            <rect x="5" y="14" width="22" height="14" rx="2" stroke={color} strokeWidth="2" fill="none" />
            {/* Awning */}
            <path d="M3 14L5 6H27L29 14" stroke={color} strokeWidth="2" fill="none" strokeLinejoin="round" />
            {/* Awning scallops */}
            <path d="M3 14C3 14 5 11 8 14C11 17 13 11 16 14C19 17 21 11 24 14C27 17 29 14 29 14" stroke={color} strokeWidth="1.5" fill="none" opacity="0.6" />
            {/* Door */}
            <rect x="13" y="20" width="6" height="8" rx="1" fill={color} opacity="0.3" />
            {/* Window left */}
            <rect x="7" y="17" width="4" height="4" rx="1" fill={color} opacity="0.2" />
            {/* Window right */}
            <rect x="21" y="17" width="4" height="4" rx="1" fill={color} opacity="0.2" />
            {/* Shopping tag */}
            <circle cx="26" cy="8" r="1.5" fill={color} opacity="0.6" />
        </svg>
    );
}

/* ─── Blast (Cross-Platform Syndication) ─── */
/* A megaphone with signal waves — represents broadcasting content */
export function BlastIcon({ size = 24, color = 'currentColor', style, className }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={style} className={className}>
            {/* Megaphone body */}
            <path d="M6 12H10L22 6V26L10 20H6V12Z" stroke={color} strokeWidth="2" fill="none" strokeLinejoin="round" />
            {/* Handle */}
            <path d="M8 20V25C8 26.1 8.9 27 10 27H12" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" />
            {/* Sound waves */}
            <path d="M25 10C27 12 27 20 25 22" stroke={color} strokeWidth="1.8" strokeLinecap="round" fill="none" opacity="0.7" />
            <path d="M28 7C31 10 31 22 28 25" stroke={color} strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.4" />
            {/* Sparkle */}
            <circle cx="26" cy="16" r="1" fill={color} opacity="0.5" />
        </svg>
    );
}
